import { FC } from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
    Box,
    Divider,
    Button,
    CircularProgress,
    Avatar,
    Chip, useTheme, Stack,
} from "@mui/material";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import useUserCardWindow from "./useUserCardWindow";
import { useLanguage } from "../../../features/localisation/useLanguage";
import {alpha} from "@mui/system";
import UserIpTable from "./UserIpTable";
import { Link } from "react-router-dom";
import {useQuery} from "../../../features/common_funcs/query_hook/useQuery";
import {useUser} from "../../../features/user/useUser";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const UserCardWindow: FC = () => {
    const { open, openedUser, isUserLoading, handleClose } = useUserCardWindow();
    const { languagePack: { langIso, pack } } = useLanguage();
    const { user: loggedUser } = useUser();
    const { userInfo, userCardWindow } = pack;
    const theme = useTheme();
    const { updateQueryParameter } = useQuery();

    if (isUserLoading) {
        return (
            <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose}>
                <DialogContent>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <CircularProgress />
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    if (!openedUser) {
        return (
            <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose}>
                <DialogContent>
                    <Typography variant="h6" align="center">
                        {userCardWindow.noUserDataMessage}
                    </Typography>
                </DialogContent>
            </Dialog>
        );
    }

    const displayName = openedUser.user_firstname && openedUser.user_lastname
        ? `${openedUser.user_firstname} ${openedUser.user_lastname}`
        : openedUser.user_name;

    const formatBalance = (balance: number) =>
        (balance / 100).toLocaleString(langIso, { minimumFractionDigits: 2 });

    return (
        <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose}>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 90, height: 90, backgroundColor: alpha(theme.palette.text.primary, 0.06) }}>
                        <AccountCircleIcon sx={{fontSize: '140px', color: alpha(theme.palette.background.default, 0.6)}} />
                    </Avatar>
                    <Box>
                        <Typography variant="h5">
                            {openedUser.user_name}
                        </Typography>
                        <Typography variant="body2">{displayName}</Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent>
                {openedUser && openedUser.parent_tree && <>
                    <Divider sx={{mb: 2}}/>

                    <Stack>
                        <Typography variant="subtitle1" gutterBottom>
                            {userCardWindow.usersPathLabel}
                        </Typography>

                        <Box
                            display={'flex'}
                            gap={1}
                            flexWrap={'wrap'}
                        >
                            {openedUser.parent_tree.map(userChip => (
                                <Chip
                                    disabled={loggedUser?.user_id === userChip.user_id}
                                    key={userChip.user_id}
                                    label={userChip.user_name}
                                    component={Link}
                                    to={`/users?showUserWithId=${userChip.user_id}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        updateQueryParameter('showUserWithId', String(userChip.user_id));
                                    }}
                                    deleteIcon={<ArrowForwardIcon/>}
                                    onDelete={() => {}}
                                />
                            ))}

                            <Chip
                                label={openedUser.user_name}
                                variant={'outlined'}
                            />
                        </Box>
                    </Stack>
                </>}

                <Divider sx={{ my: 2 }} />

                {/* Basic information */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                >
                    <Typography
                        variant="body2"
                        color="textSecondary"
                    >
                        {userInfo.emailLabel}:
                    </Typography>
                    <Typography variant="body1">
                        {openedUser.user_email || userCardWindow.notProvidedMessage}
                    </Typography>
                </Box>

                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                >
                    <Typography
                        variant="body2"
                        color="textSecondary"
                    >
                        {userInfo.phoneLabel}:
                    </Typography>
                    <Typography variant="body1">
                        {openedUser.user_phone || userCardWindow.notProvidedMessage}
                    </Typography>
                </Box>

                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                >
                    <Typography
                        variant="body2"
                        color="textSecondary"
                    >
                        {userInfo.balanceLabel}:
                    </Typography>
                    <Typography variant="body1">
                        {formatBalance(openedUser.user_balance)} {openedUser.user_currency || ""}
                    </Typography>
                </Box>

                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                >
                    <Typography
                        variant="body2"
                        color="textSecondary"
                    >
                        {userCardWindow.bonusBalanceLabel}:
                    </Typography>
                    <Typography variant="body1">
                        {openedUser.bonus_balance
                            ? `${formatBalance(openedUser.bonus_balance)} ${openedUser.user_currency || "$"}`
                            : userCardWindow.notAvailableMessage}
                    </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Additional information */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                >
                    <Typography
                        variant="body2"
                        color="textSecondary"
                    >
                        {userCardWindow.registrationDateLabel}:
                    </Typography>
                    <Typography variant="body1">
                        {openedUser.registration_time || userCardWindow.notProvidedMessage}
                    </Typography>
                </Box>

                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                >
                    <Typography
                        variant="body2"
                        color="textSecondary"
                    >
                        {userInfo.roleLabel}:
                    </Typography>
                    <Chip label={openedUser.user_role} size="small" />
                </Box>

                {/* User IP addresses */}
                <Divider sx={{ my: 2 }} />
                <UserIpTable 
                    ipAddressList={openedUser.ip_address_list} 
                    isLoading={isUserLoading} 
                />

                {/* Navigation buttons */}
                <Box display="flex" gap={2} mt={3}>
                    <Button 
                        variant="outlined" 
                        fullWidth
                        component={Link}
                        to={`/game_history?username=${openedUser.user_name}`}
                        disabled={openedUser.user_role.toLowerCase() !== "player"}
                    >
                        {userCardWindow.gameHistoryBtn}
                    </Button>
                    <Button 
                        variant="outlined" 
                        fullWidth
                        component={Link}
                        to={`/transfers/user?username=${openedUser.user_name}`}
                    >
                        {userCardWindow.transactionHistoryBtn}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default UserCardWindow;
